/**
 * ErrorBoundary — root-level React error boundary.
 *
 * Catches uncaught render errors beneath the Clerk + QueryClient
 * providers. Renders a minimal, single-column "we crashed" page with:
 *
 *   - the error message (truncated if huge)
 *   - a request id (if the thrown error carried one — rare at the
 *     render layer, but possible if a component throws a `FetchError`
 *     synchronously during render)
 *   - a "Try again" button (reload)
 *   - a "Copy details" button that copies
 *     `{error, requestId, userAgent, url, time}` as JSON
 *
 * Spec: essentials.md §6.
 *
 * Deliberately does NOT attempt a soft recovery — uncaught errors at
 * the root mean app state is broken; a reload is the safest path.
 * Router-level errors are handled separately via `errorComponent` on
 * the `createRootRoute`.
 */
import { Button } from '@/components/ui/button';
import { FetchError } from '@/lib/api';
import { ArrowClockwise, Copy, WarningOctagon } from '@phosphor-icons/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryState {
  error: Error | null;
  copied: boolean;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, copied: false };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Always log to the console — sourcemaps are shipped in prod so
    // the stack symbolicates. No external error reporter per
    // essentials.md §6 (deferred).
    console.error('[ErrorBoundary] uncaught render error', error, info);
  }

  handleReload = (): void => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  handleCopy = async (): Promise<void> => {
    const { error } = this.state;
    if (!error) return;
    const requestId =
      error instanceof FetchError && error.request_id ? error.request_id : undefined;
    const payload = {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      requestId,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      time: new Date().toISOString(),
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      this.setState({ copied: true });
      window.setTimeout(() => this.setState({ copied: false }), 2000);
    } catch {
      /* ignore */
    }
  };

  override render(): ReactNode {
    const { error, copied } = this.state;
    if (!error) return this.props.children;

    const requestId =
      error instanceof FetchError && error.request_id ? error.request_id : undefined;

    return (
      <div
        role="alert"
        className="flex min-h-dvh w-dvw items-center justify-center bg-[var(--color-paper-alt)] p-6"
      >
        <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-8 text-center shadow-[var(--shadow-panel)]">
          <WarningOctagon
            aria-hidden="true"
            size={48}
            weight="duotone"
            className="text-[var(--color-destructive)]"
          />
          <div className="space-y-1">
            <h1 className="text-lg font-semibold text-[var(--color-ink)]">Something went wrong</h1>
            <p className="text-sm text-[var(--color-mute)]">
              The dashboard hit an unexpected error. Reloading usually fixes it; if not, paste the
              details below into a support note.
            </p>
          </div>
          <pre className="max-h-32 w-full overflow-auto rounded-md border border-[var(--color-line-soft)] bg-[var(--color-paper-alt)] p-3 text-left font-mono text-xs text-[var(--color-ink)]">
            {error.message || error.name}
          </pre>
          {requestId && (
            <p className="font-mono text-xs text-[var(--color-mute)]">Request id: {requestId}</p>
          )}
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button onClick={this.handleReload}>
              <ArrowClockwise aria-hidden="true" />
              Try again
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                void this.handleCopy();
              }}
            >
              <Copy aria-hidden="true" />
              {copied ? 'Copied' : 'Copy details'}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
